// GENERATED FILE — DO NOT EDIT.
// Source: hsos-shared/src/analytics/ — edit there, then run:
//   node scripts/sync-shared-modules.mjs
// Target: hsos-web vendored @hsos/shared

/**
 * Feature-flag and experiment registry.
 *
 * Flags are declared here so a call site cannot invent a key that does not
 * exist in PostHog (a typo silently returns `false`, which reads as "feature
 * off" and is the worst possible failure mode for a kill switch).
 *
 * Every flag declares a `fallback` — the value used when PostHog is
 * unreachable, the key is not yet configured, or the visitor declined
 * analytics consent. Kill switches therefore fall back to *enabled* (the
 * feature keeps working if analytics is down) while rollouts fall back to
 * *disabled* (nothing ships to everyone by accident).
 */

export type FlagKind =
  /** Turns an existing capability off in an incident. Fallback: on. */
  | "kill_switch"
  /** Gradual rollout of new functionality. Fallback: off. */
  | "rollout"
  /** Multivariate A/B test backed by a PostHog experiment. Fallback: control. */
  | "experiment";

export type FlagDefinition = {
  readonly kind: FlagKind;
  readonly description: string;
  /** Value returned when PostHog cannot answer. */
  readonly fallback: boolean | string;
  /** Declared variants for experiments; omitted for boolean flags. */
  readonly variants?: readonly string[];
  /** True when the flag must be evaluated per-organization, not per-user. */
  readonly groupScoped?: boolean;
};

export const FEATURE_FLAGS = {
  "session-replay-console": {
    kind: "rollout",
    description:
      "Records session replay inside the authenticated console. Off by default because console replays capture tenant customer data — enable per-org only with the tenant's agreement.",
    fallback: false,
    groupScoped: true,
  },
  "ai-autonomy-level-3": {
    kind: "rollout",
    description:
      "Allows an org to graduate to fully autonomous AI handling (no human confirmation on send).",
    fallback: false,
    groupScoped: true,
  },
  "copilot-suggestions": {
    kind: "rollout",
    description: "Shows AI-drafted replies to operators in the console inbox.",
    fallback: false,
    groupScoped: true,
  },
  "kb-hybrid-search": {
    kind: "rollout",
    description:
      "Runs the combined vector + full-text retrieval arm instead of vector-only.",
    fallback: false,
    groupScoped: true,
  },
  "voice-agent": {
    kind: "rollout",
    description: "Enables the Retell-backed voice agent for an organization.",
    fallback: false,
    groupScoped: true,
  },
  "founder-analytics-live": {
    kind: "rollout",
    description:
      "Serves the founder analytics dashboard from the live PostHog query API rather than cached Postgres rollups.",
    fallback: false,
  },
  "onboarding-express-track": {
    kind: "experiment",
    description:
      "Tests a shortened three-step onboarding against the full setup wizard.",
    fallback: "control",
    variants: ["control", "express"],
  },
  "pricing-page-2026q3": {
    kind: "experiment",
    description: "Tests pricing page presentation on the marketing site.",
    fallback: "control",
    variants: ["control", "value-first", "usage-first"],
  },
  "kill-ai-sending": {
    kind: "kill_switch",
    description:
      "Master switch for AI-composed outbound messages. Turn OFF in PostHog to make the orchestrator draft-only platform-wide without a deploy.",
    fallback: true,
  },
  "kill-outreach-sms": {
    kind: "kill_switch",
    description:
      "Master switch for the outbound growth SMS pipeline. Turn OFF to halt all outreach sends immediately.",
    fallback: true,
  },
} as const satisfies Record<string, FlagDefinition>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

export const FEATURE_FLAG_KEYS = Object.keys(
  FEATURE_FLAGS,
) as FeatureFlagKey[];

/**
 * Widen a registry entry to `FlagDefinition` — `satisfies` keeps each entry's
 * literal type, so `variants` is absent on boolean flags without this.
 */
export function flagDefinition(key: FeatureFlagKey): FlagDefinition {
  return FEATURE_FLAGS[key];
}

export function flagFallback(key: FeatureFlagKey): boolean | string {
  return flagDefinition(key).fallback;
}

/** Narrow a raw PostHog flag value to the declared shape, else the fallback. */
export function resolveFlagValue(
  key: FeatureFlagKey,
  raw: boolean | string | undefined,
): boolean | string {
  const definition = flagDefinition(key);

  if (raw === undefined) return definition.fallback;

  if (definition.variants) {
    return typeof raw === "string" &&
      (definition.variants as readonly string[]).includes(raw)
      ? raw
      : definition.fallback;
  }

  return typeof raw === "boolean" ? raw : definition.fallback;
}
